import React, { useState, useEffect, useCallback } from 'react';
import {
  MagnifyingGlassIcon,
  ArrowDownTrayIcon,
  EyeIcon,
  ChevronLeftIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { apiClient } from '../../services/api';

const SubscriptionHistoryAdmin = () => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedEventType, setSelectedEventType] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // Modal
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  // Event types for filter
  const eventTypes = [
    { value: 'created', label: '🆕 Created' },
    { value: 'renewed', label: '🔄 Renewed' },
    { value: 'upgraded', label: '⬆️ Upgraded' },
    { value: 'downgraded', label: '⬇️ Downgraded' },
    { value: 'extended', label: '📅 Extended' },
    { value: 'activated', label: '✅ Activated' },
    { value: 'deactivated', label: '❌ Deactivated' },
    { value: 'cancelled', label: '🚫 Cancelled' },
    { value: 'expired', label: '⏰ Expired' },
    { value: 'reinstated', label: '♻️ Reinstated' }
  ];

  const statuses = [
    { value: 'active', label: 'Active', color: 'green' },
    { value: 'inactive', label: 'Inactive', color: 'gray' },
    { value: 'expired', label: 'Expired', color: 'red' },
    { value: 'cancelled', label: 'Cancelled', color: 'red' }
  ];

  const loadHistory = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage,
        limit: 20,
        ...(searchTerm && { search: searchTerm }),
        ...(selectedEventType && { event_type: selectedEventType }),
        ...(selectedStatus && { status: selectedStatus }),
        ...(dateFrom && { date_from: dateFrom }),
        ...(dateTo && { date_to: dateTo })
      });

      const response = await apiClient.get(`/admin/subscription-history/all?${params}`);
      setHistory(response.data.data);
      setTotalPages(response.data.pagination.pages);
      setTotalRecords(response.data.pagination.total);
    } catch (error) {
      console.error('Failed to load subscription history:', error);
      toast.error('Failed to load subscription history');
    } finally {
      setLoading(false);
    }
  }, [currentPage, dateFrom, dateTo, searchTerm, selectedEventType, selectedStatus]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const handleSearch = (value) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };

  const handleFilterChange = (type, value) => {
    setCurrentPage(1);
    switch (type) {
      case 'eventType':
        setSelectedEventType(value);
        break;
      case 'status':
        setSelectedStatus(value);
        break;
      case 'dateFrom':
        setDateFrom(value);
        break;
      case 'dateTo':
        setDateTo(value);
        break;
      default:
        break;
    }
  };

  const handleExport = async (format) => {
    try {
      const params = new URLSearchParams({
        format,
        search: searchTerm,
        event_type: selectedEventType
      });

      const response = await apiClient.get(
        `/admin/subscription-history/export?${params}`,
        { responseType: format === 'csv' ? 'blob' : 'json' }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `subscription-history.${format}`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);

      toast.success(`Exported as ${format.toUpperCase()}`);
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Failed to export data');
    }
  };

  const getEventBadge = (eventType) => {
    const badges = {
      created: { emoji: '🆕', color: 'blue', label: 'Created' },
      renewed: { emoji: '🔄', color: 'green', label: 'Renewed' },
      upgraded: { emoji: '⬆️', color: 'purple', label: 'Upgraded' },
      downgraded: { emoji: '⬇️', color: 'orange', label: 'Downgraded' },
      extended: { emoji: '📅', color: 'cyan', label: 'Extended' },
      activated: { emoji: '✅', color: 'green', label: 'Activated' },
      deactivated: { emoji: '❌', color: 'red', label: 'Deactivated' },
      cancelled: { emoji: '🚫', color: 'red', label: 'Cancelled' },
      expired: { emoji: '⏰', color: 'red', label: 'Expired' },
      reinstated: { emoji: '♻️', color: 'green', label: 'Reinstated' }
    };
    return badges[eventType] || badges.created;
  };

  const getStatusBadge = (status) => {
    const badges = {
      active: { bg: 'bg-green-100', text: 'text-green-800', label: 'Active' },
      inactive: { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Inactive' },
      expired: { bg: 'bg-red-100', text: 'text-red-800', label: 'Expired' },
      cancelled: { bg: 'bg-red-100', text: 'text-red-800', label: 'Cancelled' }
    };
    return badges[status] || badges.inactive;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const ViewDetailsModal = () => {
    if (!selectedRecord) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-blue-700 text-white p-6 border-b">
            <div className="flex justify-between items-center">
              <h3 className="text-xl font-semibold">Subscription History Details</h3>
              <button
                onClick={() => {
                  setShowDetailsModal(false);
                  setSelectedRecord(null);
                }}
                className="text-white hover:bg-white hover:bg-opacity-20 rounded-lg p-1"
              >
                ✕
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 space-y-6">
            {/* User Information */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="font-semibold text-gray-900 mb-3">User Information</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600">Business Name</p>
                  <p className="font-medium">{selectedRecord.business_name}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Email</p>
                  <p className="font-medium text-blue-600">{selectedRecord.email}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">User ID</p>
                  <p className="font-medium">#{selectedRecord.user_id}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Event Date</p>
                  <p className="font-medium">{formatDate(selectedRecord.created_at)}</p>
                </div>
              </div>
            </div>

            {/* Event Information */}
            <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
              <h4 className="font-semibold text-gray-900 mb-3">Event Information</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600">Event Type</p>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-2xl">{getEventBadge(selectedRecord.event_type).emoji}</span>
                    <span className="font-medium">{getEventBadge(selectedRecord.event_type).label}</span>
                  </div>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Subscription Status</p>
                  <div className="flex items-center gap-2 mt-1">
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusBadge(selectedRecord.subscription_status).bg} ${getStatusBadge(selectedRecord.subscription_status).text}`}>
                      {getStatusBadge(selectedRecord.subscription_status).label}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Subscription Details */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="font-semibold text-gray-900 mb-3">Current Subscription Details</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600">License Type</p>
                  <p className="font-medium">{selectedRecord.license_type || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Plan Name</p>
                  <p className="font-medium">{selectedRecord.plan_name || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Max Invoices/Month</p>
                  <p className="font-medium">{selectedRecord.max_invoices_per_month || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Expires At</p>
                  <p className="font-medium">{formatDate(selectedRecord.subscription_expires_at)}</p>
                </div>
              </div>
            </div>

            {/* Previous Values */}
            {selectedRecord.previous_license_type && (
              <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
                <h4 className="font-semibold text-gray-900 mb-3">Previous Values</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Previous License Type</p>
                    <p className="font-medium text-yellow-700">{selectedRecord.previous_license_type || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Previous Status</p>
                    <p className="font-medium text-yellow-700">{selectedRecord.previous_status || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Previous Max Invoices</p>
                    <p className="font-medium text-yellow-700">{selectedRecord.previous_max_invoices || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Previous Expiry</p>
                    <p className="font-medium text-yellow-700">{formatDate(selectedRecord.previous_expires_at)}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Admin Action */}
            {selectedRecord.admin_id && (
              <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                <h4 className="font-semibold text-gray-900 mb-3">Admin Action</h4>
                <div className="space-y-3">
                  <div>
                    <p className="text-sm text-gray-600">Admin</p>
                    <p className="font-medium">{selectedRecord.admin_name || 'System'}</p>
                  </div>
                  {selectedRecord.admin_notes && (
                    <div>
                      <p className="text-sm text-gray-600">Notes</p>
                      <p className="font-medium text-purple-700">{selectedRecord.admin_notes}</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="bg-gray-50 p-6 border-t flex justify-end">
            <button
              onClick={() => {
                setShowDetailsModal(false);
                setSelectedRecord(null);
              }}
              className="px-4 py-2 bg-gray-300 text-gray-800 rounded-lg hover:bg-gray-400 font-medium"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (loading && history.length === 0) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Subscription History</h2>
          <p className="text-sm text-gray-600 mt-1">Track all subscription events and changes</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => handleExport('csv')}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
          >
            <ArrowDownTrayIcon className="w-4 h-4" />
            CSV
          </button>
          <button
            onClick={() => handleExport('json')}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
          >
            <ArrowDownTrayIcon className="w-4 h-4" />
            JSON
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-6 space-y-4">
        {/* Search Bar */}
        <div className="relative">
          <MagnifyingGlassIcon className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search by business name or email..."
            value={searchTerm}
            onChange={(e) => handleSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        {/* Filter Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          {/* Event Type Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Event Type</label>
            <select
              value={selectedEventType}
              onChange={(e) => handleFilterChange('eventType', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
            >
              <option value="">All Events</option>
              {eventTypes.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>

          {/* Status Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              value={selectedStatus}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
            >
              <option value="">All Statuses</option>
              {statuses.map((status) => (
                <option key={status.value} value={status.value}>
                  {status.label}
                </option>
              ))}
            </select>
          </div>

          {/* Date From */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">From Date</label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
            />
          </div>

          {/* Date To */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">To Date</label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => handleFilterChange('dateTo', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
            />
          </div>

          {/* Clear Filters */}
          <div className="flex items-end">
            <button
              onClick={() => {
                setSearchTerm('');
                setSelectedEventType('');
                setSelectedStatus('');
                setDateFrom('');
                setDateTo('');
                setCurrentPage(1);
              }}
              className="w-full px-3 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition text-sm font-medium"
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Results Info */}
      <div className="text-sm text-gray-600">
        Showing <span className="font-semibold">{(currentPage - 1) * 20 + 1}</span> to{' '}
        <span className="font-semibold">{Math.min(currentPage * 20, totalRecords)}</span> of{' '}
        <span className="font-semibold">{totalRecords}</span> records
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Event</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Business Name</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Email</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">License Type</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Expires At</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Date</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {history.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-6 py-8 text-center text-gray-500">
                    No subscription history found
                  </td>
                </tr>
              ) : (
                history.map((record) => (
                  <tr key={record.id} className="hover:bg-gray-50 transition">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <span className="text-xl">{getEventBadge(record.event_type).emoji}</span>
                        <span className="text-sm font-medium text-gray-700">
                          {getEventBadge(record.event_type).label}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">{record.business_name}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">{record.email}</td>
                    <td className="px-6 py-4">
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {record.license_type || 'N/A'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusBadge(record.subscription_status).bg} ${getStatusBadge(record.subscription_status).text}`}>
                        {getStatusBadge(record.subscription_status).label}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {formatDate(record.subscription_expires_at)}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {formatDate(record.created_at)}
                    </td>
                    <td className="px-6 py-4 text-center">
                      <button
                        onClick={() => {
                          setSelectedRecord(record);
                          setShowDetailsModal(true);
                        }}
                        className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 transition"
                      >
                        <EyeIcon className="w-4 h-4" />
                        View
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-between items-center">
          <div className="text-sm text-gray-600">
            Page <span className="font-semibold">{currentPage}</span> of{' '}
            <span className="font-semibold">{totalPages}</span>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="flex items-center gap-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition"
            >
              <ChevronLeftIcon className="w-4 h-4" />
              Previous
            </button>
            <button
              onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              className="flex items-center gap-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition"
            >
              Next
              <ChevronRightIcon className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Details Modal */}
      {showDetailsModal && <ViewDetailsModal />}
    </div>
  );
};

export default SubscriptionHistoryAdmin;
